import React from 'react'
// libs
import { useMutation, useQuery } from '@apollo/client/react'
import { useSnackbar } from 'notistack'
// components
import { Button, BUTTON_LAYOUTS, CREATE_BUTTON_STYLE } from 'components/UI'
// graphql
import { INJURY_MASTER_SYNC_MUTATION } from 'graphql/injuryMaster/mutations'
import { INJURY_MASTER_SETTINGS_QUERY, INJURY_MASTER_SYNC_RUN_QUERY } from 'graphql/injuryMaster/queries'
// types
import { IInjuryMasterSettings } from 'types/graphql'
// utils
import sumBy from 'lodash/sumBy'
import pluralize from 'pluralize'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

const POLL_INTERVAL_MS = 2000
// Upper end of the 3-4 seconds a queued item takes to actually sync.
const SECONDS_PER_QUEUED_ITEM = 4

enum SyncRunStatus {
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  COMPLETED_WITH_ERRORS = 'COMPLETED_WITH_ERRORS',
  FAILED = 'FAILED'
}

interface ISyncProgress {
  total: number
  synced: number
  skipped: number
  failed: number
}

interface ISyncRun {
  id: string
  status: SyncRunStatus
  progress: ISyncProgress[]
}

const isActive = (status?: SyncRunStatus): boolean =>
  status === SyncRunStatus.QUEUED || status === SyncRunStatus.RUNNING

const totalOf = (progress: ISyncProgress[], key: keyof ISyncProgress): number => sumBy(progress, key)

const SyncInjuryMaster: React.FC = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const { data: settingsData } = useQuery<{ injuryMasterSettings: IInjuryMasterSettings }>(INJURY_MASTER_SETTINGS_QUERY)

  const isConfigured = !!settingsData?.injuryMasterSettings?.accountId

  // The latest run is read on mount too, so a run started before a page reload is picked up again.
  const { data, startPolling, stopPolling } = useQuery<{ injuryMasterSyncRun: ISyncRun }>(
    INJURY_MASTER_SYNC_RUN_QUERY,
    {
      fetchPolicy: 'network-only'
    }
  )

  const run = data?.injuryMasterSyncRun
  const active = isActive(run?.status)

  const [startSync, { loading: isStarting }] = useMutation(INJURY_MASTER_SYNC_MUTATION, {
    refetchQueries: [INJURY_MASTER_SYNC_RUN_QUERY]
  })

  React.useEffect(() => {
    if (active) {
      startPolling(POLL_INTERVAL_MS)
    } else {
      stopPolling()
    }

    return () => stopPolling()
  }, [active, startPolling, stopPolling])

  // Tell the user how it ended — only when this page saw the run go from active to over, not for an
  // old run found on load.
  const previousStatus = React.useRef<SyncRunStatus | undefined>(undefined)

  React.useEffect(() => {
    const status = run?.status

    if (isActive(previousStatus.current) && status && !isActive(status)) {
      const progress = run?.progress ?? []
      const synced = totalOf(progress, 'synced')
      const skipped = totalOf(progress, 'skipped')
      const failed = totalOf(progress, 'failed')

      if (status === SyncRunStatus.FAILED) {
        enqueueSnackbar('Injury Master sync failed. Check the connection and try again.', {
          variant: 'error',
          autoHideDuration: null,
          action: (key) => getSnackbarCloseButton(key, closeSnackbar)
        })
      } else if (failed > 0) {
        enqueueSnackbar(
          `Injury Master sync finished: ${synced} synced, ${failed} failed. Sync again to retry the failed ${pluralize('record', failed)}.`,
          { variant: 'warning' }
        )
      } else {
        // "Synced" from the back end only means the record is queued; each one still takes a few seconds.
        const estimateMin = Math.ceil((synced * SECONDS_PER_QUEUED_ITEM) / 60)

        enqueueSnackbar(
          `Injury Master sync queued: ${synced} ${pluralize('record', synced)}${skipped ? `, ${skipped} skipped` : ''}. ` +
            `Each takes about 3-4 seconds to sync, so this may take up to ${estimateMin} ${pluralize('minute', estimateMin)}.`,
          { variant: 'success' }
        )
      }
    }

    previousStatus.current = status
  }, [run?.status, run?.progress, enqueueSnackbar, closeSnackbar])

  const onClick = () => startSync()

  const progress = run?.progress ?? []
  const total = totalOf(progress, 'total')
  const done = totalOf(progress, 'synced') + totalOf(progress, 'skipped') + totalOf(progress, 'failed')

  return (
    <Button
      style={CREATE_BUTTON_STYLE}
      layout={BUTTON_LAYOUTS.PURPLE}
      onClick={onClick}
      isLoading={isStarting || active}
      disabled={!isConfigured || active}>
      {active ? `Syncing ${done}/${total}` : 'Sync'}
    </Button>
  )
}

export default SyncInjuryMaster
